/**
 * funnelRows.ts — the "Deals by stage" rows' view model, shared by Sales
 * Dashboard and Reports (§24 C-6 / C-7).
 *
 * The wire is the server's fixed 5-row spine (open stages + closed_won,
 * zero-filled). Two rulings shape what the rows say about it:
 *
 *   C-6  The four open rows are a STOCK — deals sitting in each stage now —
 *        so `count / max` is the right rule (it is a distribution, not a
 *        flow), but the max is taken over the OPEN rows ONLY. Top-stage
 *        scaling was refused: Fourth Coffee's top row is 0 (÷0) and any later
 *        stage larger than the first would exceed 100 %.
 *   C-7  Closed Won stays as the fifth row (§10.D parity; the Dashboard and
 *        the Reports rows reconcile with the Archive by construction) but
 *        LEAVES the shared track: it is a cumulative all-time flow that can
 *        only grow, and on one scale it swallows the stock — six wins made
 *        the open rows stubs on Fourth Coffee. No bar; the count with its
 *        qualifier ("6 won · all-time") in the success ink (S-Tier --ok).
 *
 * Fills bind BY STAGE LABEL through stageDot — the board's own binding (Home
 * tabs H-1b, RULED 2026-10-18: one stage colour everywhere) — so a zero row
 * never shifts a colour AND the donut, the Reports rows and the Pipeline
 * board agree on every stage. Until H-1b the fills came from `stageBar` by
 * spine index, which disagreed with the board on three of four stages. A
 * slug the shared map does not know gets the neutral dot.
 */
import { SLUG_TO_STAGE } from '../../api'
import { stageDotFor } from '../../stageDotFor'
import { success } from '../../../../theme/tokens'

export const CLOSED_WON_STAGE = 'closed_won'

export interface FunnelCount { stage: string; count: number }

export type FunnelRow =
  | { kind: 'open'; stage: string; label: string; count: number; widthPct: number; fill: string }
  | { kind: 'won'; stage: string; label: string; count: number; qualifier: string; ink: string }

/** "6 won · all-time" — the count says what it counts and over what window
 *  (the funnel's closed_won is COUNT(*) with no window, module.go). */
export function wonQualifier(count: number): string {
  return `${count} won · all-time`
}

/** The max the open tracks scale to: over the open rows only, never below 1
 *  (an all-zero funnel draws four empty tracks, not NaN). */
export function openMax(funnel: FunnelCount[]): number {
  return Math.max(1, ...funnel.filter(f => f.stage !== CLOSED_WON_STAGE).map(f => f.count))
}

export function funnelRows(funnel: FunnelCount[]): FunnelRow[] {
  const max = openMax(funnel)
  return funnel.map(f => {
    const label = SLUG_TO_STAGE[f.stage] ?? f.stage
    if (f.stage === CLOSED_WON_STAGE) {
      return { kind: 'won', stage: f.stage, label, count: f.count, qualifier: wonQualifier(f.count), ink: success.DEFAULT }
    }
    return { kind: 'open', stage: f.stage, label, count: f.count, widthPct: (f.count / max) * 100, fill: stageDotFor(label) }
  })
}
